import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import SVG from "react-inlinesvg";

import iconUpsellModalSelectIcon from "../../../../img/icons/upsell-modal-select-icon.svg";
import iconUpsellModalSelectedIcon from "../../../../img/icons/upsell-modal-selected-icon.svg";
import * as productsAPI from "../../../api/products";
import { Flair } from "../../../common/Flair";
import { Image } from "../../../common/Image";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { RichText } from "../../../common/RichText";
import { ValuePropsBar } from "../../../common/ValuePropsBar";
import { BundleGroupTypes } from "../../../constants";
import {
    IAddon,
    IConcreteBundle,
    IProduct,
    InternalProduct,
} from "../../../models/catalogue";
import { IProductAPIURL, IProductID } from "../../../models/nominals";
import { IValuePropBarBlock } from "../../../models/value-props";
import { PriceType, getProductPrice } from "../../../utils/catalogue";
import { format } from "../../../utils/format";
import { ZERO } from "../../../utils/money";
import { assertNever } from "../../../utils/never";
import {
    IModularConfiguratorUpsellInfoModal,
    IModularConfigurator_UpsellInfoModalContent,
    IModularConfigurator_UpsellInfoModalProduct,
    IUpsellInfoModalFeaturesBlock,
    IUpsellInfoModalImageGallery,
    IUpsellInfoModalImageSlide,
    IUpsellInfoModalRichTextBlock,
    IUpsellInfoModalSegmentedRichTextBlock,
} from "../models";
import { getUpgradeID } from "../selectors";

import styles from "./ConfiguratorUpsellInfoModal.module.scss";

const UpsellInfoModalImageSlide = (props: {
    slide: IUpsellInfoModalImageSlide;
}) => {
    if (!props.slide.image) {
        return null;
    }
    const url =
        props.slide.image.renditions?.desktop_img?.url ?? props.slide.image.url;
    return (
        <Image
            className={styles.imageSlide}
            src={url}
            alt={props.slide.image.title}
            lazy={false}
        />
    );
};

const initFlickity = (
    element: HTMLDivElement | null,
    flktyRef: React.MutableRefObject<Flickity | null>,
) => {
    if (!element) {
        return;
    }
    if (flktyRef.current) {
        flktyRef.current.destroy();
    }
    flktyRef.current = new Flickity(element, {
        cellAlign: "left",
        pageDots: true,
        prevNextButtons: true,
        imagesLoaded: true,
    });
};

const UpsellInfoModalImageGallery = (props: {
    block: IUpsellInfoModalImageGallery;
}): React.ReactNode => {
    const imageListElem = React.useRef<HTMLDivElement>(null);
    const flktyRef = React.useRef<Flickity | null>(null);

    React.useEffect(() => {
        initFlickity(imageListElem.current, flktyRef);

        return () => {
            if (flktyRef.current) {
                flktyRef.current.destroy();
                flktyRef.current = null;
            }
        };
    });

    if (props.block.slides.length === 1) {
        const slide = props.block.slides[0];
        return (
            <Image
                className={styles.imageSlide}
                src={slide.image?.url}
                alt={slide.image?.title}
                lazy={false}
            />
        );
    }
    return (
        <div className={styles.imageGallery}>
            <div ref={imageListElem}>
                {props.block.slides.map((slide, idx) => (
                    <UpsellInfoModalImageSlide key={idx} slide={slide} />
                ))}
            </div>
        </div>
    );
};

const UpsellInfoModalFeaturesBlock = (props: {
    isOneProduct: boolean;
    block: IUpsellInfoModalFeaturesBlock;
}) => {
    return (
        <div>
            <h3 className={styles.featuresHeading}>{props.block.heading}</h3>
            <ul className={styles.featuresList}>
                {props.block.features.map((feature, idx) => (
                    <li key={idx} className={styles.featuresItem}>
                        {props.block.list_icon && (
                            <Image
                                className={styles.featuresIcon}
                                src={props.block.list_icon?.url}
                                alt=""
                            />
                        )}
                        <span>{feature.content}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const UpsellInfoModalSegmentedRichTextBlock = (props: {
    block: IUpsellInfoModalSegmentedRichTextBlock;
}) => {
    return (
        // TODO: Temporary CSS class to allow site-specific styling
        <div className={styles.segmentedRichText}>
            {props.block.segments.map((rich_text, idx) => {
                return <RichText key={idx} html={rich_text} />;
            })}
        </div>
    );
};

const UpsellInfoModalRichTextBlock = (props: {
    block: IUpsellInfoModalRichTextBlock;
}) => {
    return (
        <div className={styles.upsellRichTextBlock}>
            {props.block.heading && (
                <h3 className={styles.featuresHeading}>
                    {props.block.heading}
                </h3>
            )}
            {props.block.content && <RichText html={props.block.content} />}
        </div>
    );
};

const UpsellInfoModalValueProps = (props: { block: IValuePropBarBlock }) => {
    return (
        <div className={styles.valueProps}>
            <ValuePropsBar
                className="upsell-info-modal"
                valueProps={props.block.value_props}
            />
        </div>
    );
};

const UpsellInfoModalBlock = (props: {
    isOneProduct: boolean;
    block: IModularConfigurator_UpsellInfoModalContent;
}) => {
    switch (props.block.type) {
        case "features":
            return (
                <UpsellInfoModalFeaturesBlock
                    isOneProduct={props.isOneProduct}
                    block={props.block.value}
                />
            );
        case "image_gallery":
            return <UpsellInfoModalImageGallery block={props.block.value} />;
        case "rich_text":
            return <UpsellInfoModalRichTextBlock block={props.block.value} />;
        case "segmented_rich_text":
            return (
                <UpsellInfoModalSegmentedRichTextBlock
                    block={props.block.value}
                />
            );
        case "value_props":
            return <UpsellInfoModalValueProps block={props.block.value} />;
    }
    assertNever(props.block);
    return null;
};

const UpsellInfoColumnProductInfo = (props: {
    product: IProduct;
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    bundleType: string;
    isOneProduct?: boolean | null;
}) => {
    const buildImage = (product: InternalProduct) => {
        const productImage = product.parent
            ? product.parent.images[0]
            : product.images[0];

        if (productImage) {
            return productImage.original;
        }

        return "";
    };

    const buildPrice = (product: InternalProduct) => {
        if (props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT) {
            const price = getProductPrice(product.price, {
                priceType: PriceType.RETAIL,
                includePostDiscountAddons: true,
                quantity: 1,
            });
            return `(${format.money(price)} Value)`;
        }

        let basePrice = ZERO;
        if (
            props.baseVariant &&
            (props.bundleType === BundleGroupTypes.UPGRADE_PRODUCT ||
                props.bundleType === BundleGroupTypes.UPGRADE_MODEL)
        ) {
            basePrice = getProductPrice(props.baseVariant.price, {
                priceType: PriceType.COSMETIC_EXCL_TAX,
                includePostDiscountAddons: true,
                quantity: 1,
            });
        }
        const upgradePrice = getProductPrice(product.price, {
            priceType: PriceType.COSMETIC_EXCL_TAX,
            includePostDiscountAddons: true,
            quantity: 1,
        });
        const priceDiff = upgradePrice.subtract(basePrice);

        if (!props.isOneProduct) {
            if (
                priceDiff.isZero() ||
                priceDiff.equalsTo(basePrice.multiply(-1))
            ) {
                return "";
            } else if (priceDiff.isNegative()) {
                return format.money(priceDiff.multiply(-1));
            }
            return `+ ${format.money(priceDiff)}`;
        }
        return `+ ${format.money(priceDiff)}`;
    };

    const buildUpsellProductName = (product: InternalProduct) => {
        if (product.parent) {
            if (product.parent.attributes.upgrade_option_title) {
                return product.parent.attributes.upgrade_option_title.value;
            }
            return product.parent.title;
        }

        if (product.attributes.upgrade_option_title) {
            return product.attributes.upgrade_option_title.value;
        }

        return product.title;
    };

    const ratingClasses = classNames({
        [styles.rating]: true,
        [styles.freeGiftRating]:
            props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT,
    });

    const productNameClasses = classNames({
        [styles.productName]: true,
        [styles.freeGiftProductName]:
            props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT,
    });

    return (
        <>
            <Image
                className={styles.productImage}
                src={buildImage(props.product)}
                alt=""
            />
            <h2 className={productNameClasses}>
                {buildUpsellProductName(props.product)}
            </h2>
            {props.product.rating && props.product.rating > 0 && (
                <RatingGraphic
                    starRatingClass={ratingClasses}
                    cardClass="tile"
                    cardSize="large"
                    numReviews={props.product.num_reviews}
                    rating={props.product.rating}
                />
            )}
            {props.bundleType !==
                BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT && (
                <p className={styles.price}>{buildPrice(props.product)}</p>
            )}
        </>
    );
};

export const UpsellInfoColumnContent = (props: {
    isOneProduct: boolean;
    content: readonly IModularConfigurator_UpsellInfoModalContent[];
}) => {
    return (
        <>
            {props.content.map((block) => (
                <UpsellInfoModalBlock
                    key={block.id}
                    isOneProduct={props.isOneProduct}
                    block={block}
                />
            ))}
        </>
    );
};

interface IColumnProps {
    column: IModularConfigurator_UpsellInfoModalProduct;
    isOneProduct: boolean;
    isFreeGift: boolean;
    bundleID: number;
    bundleType: string;
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    selectedAddons: readonly IAddon[];
    selectedUpgrade: IProductID | null;
    upgradeBundles: readonly IConcreteBundle[];
    updateSelectedAddons: (
        updates: Array<{ productID: IProductID; quantity: number }>,
    ) => void;
    setSelectedUpgrade: (selectedUpgrade: IProductID | null) => void;
}

const ANALYTICS_CLASS_NAMES: { [key: string]: string } = {
    [BundleGroupTypes.IN_CONFIGURATOR_ADD_ON]:
        "al-add-on-card-panel-select-button",
    [BundleGroupTypes.UPGRADE_MODEL]:
        "al-change-model-upgrade-panel-select-button",
    [BundleGroupTypes.UPGRADE_PRODUCT]:
        "al-standard-upgrade-card-panel-select-button",
};

const loadProductAndBundle = async (
    columnProduct: IProductAPIURL,
    bundleID: number,
    upgradeBundles: readonly IConcreteBundle[],
): Promise<{
    product: IProduct;
    bundle: IConcreteBundle | null;
}> => {
    const product = await productsAPI.getProduct(columnProduct);

    const thisBundle = upgradeBundles.find((b) => {
        return b.bundle_group.id === bundleID;
    });

    if (thisBundle) {
        const variantProduct = thisBundle.suggested_products.find((p) => {
            return p.parent?.id === product.id;
        });

        return {
            product: variantProduct || product,
            bundle: thisBundle,
        };
    }

    return {
        product,
        bundle: null,
    };
};

const checkIsSelected = (
    product: IProduct | null,
    bundleType: string,
    selectedRoot: IProduct | null,
    selectedUpgrade: IProductID | null,
    selectedAddons: readonly IAddon[],
): boolean => {
    const upgradeID = product ? getUpgradeID(product) : null;

    if (bundleType === BundleGroupTypes.UPGRADE_MODEL) {
        return selectedRoot !== null && selectedRoot.id === upgradeID;
    }

    if (bundleType === BundleGroupTypes.UPGRADE_PRODUCT) {
        return selectedUpgrade === upgradeID;
    }

    const selectedAddon = selectedAddons.find((addon) => {
        return addon.productID === upgradeID;
    });
    return selectedAddon !== undefined;
};

const UpsellInfoColumn = (props: IColumnProps): React.ReactNode => {
    const [product, setProduct] = React.useState<IProduct | null>(null);
    const [baseVariant, setBaseVariant] = React.useState<IProduct | null>(null);
    const [bundle, setBundle] = React.useState<IConcreteBundle | null>(null);
    const didLoadProductRef = React.useRef(false);
    const didCaptureBaseVariantRef = React.useRef(false);

    React.useEffect(() => {
        if (didLoadProductRef.current) {
            return;
        }
        didLoadProductRef.current = true;

        const loadData = async () => {
            const result = await loadProductAndBundle(
                props.column.product,
                props.bundleID,
                props.upgradeBundles,
            );
            setProduct(result.product);
            setBundle(result.bundle);
        };
        void loadData();
    }, [props.column.product, props.bundleID, props.upgradeBundles]);

    React.useEffect(() => {
        if (didCaptureBaseVariantRef.current) {
            return;
        }
        didCaptureBaseVariantRef.current = true;

        if (props.baseVariant) {
            setBaseVariant(props.baseVariant);
        }
    }, [props.baseVariant]);

    const onChange = (e: React.FormEvent<HTMLInputElement>) => {
        if (!product) {
            return;
        }

        const upgradeID = getUpgradeID(product);

        // If this is an UPGRADE_PRODUCT or UPGRADE_MODEL Bundle, we update things one way...
        if (
            props.bundleType === BundleGroupTypes.UPGRADE_PRODUCT ||
            props.bundleType === BundleGroupTypes.UPGRADE_MODEL
        ) {
            props.setSelectedUpgrade(
                e.currentTarget.checked ? upgradeID : null,
            );
            // If it's another kind of Bundle, we update things a different way...
        } else {
            const updates = [
                {
                    productID: upgradeID,
                    quantity: e.currentTarget.checked ? 1 : 0,
                },
            ];
            if (bundle) {
                for (const bundleProduct of bundle.suggested_products) {
                    const pID = getUpgradeID(bundleProduct);
                    if (pID !== upgradeID) {
                        updates.push({
                            productID: pID,
                            quantity: 0,
                        });
                    }
                }
            }
            props.updateSelectedAddons(updates);
        }
    };

    const buildUI = () => {
        const upgradeID = product ? getUpgradeID(product) : null;

        if (
            // if the currently selected Root product matches the upgradeID,
            // AND this isn't an UPGRADE_MODEL bundle,
            // then don't show any UI
            props.selectedRoot &&
            props.bundleType !== BundleGroupTypes.UPGRADE_MODEL &&
            props.selectedRoot.id === upgradeID
        ) {
            return false;
        }

        const isSelected = checkIsSelected(
            product,
            props.bundleType,
            props.selectedRoot,
            props.selectedUpgrade,
            props.selectedAddons,
        );

        const textLabel = isSelected ? "Selected" : "Select";
        const icon = isSelected ? (
            <SVG
                className={styles.selectedIcon}
                src={iconUpsellModalSelectedIcon}
            />
        ) : (
            <SVG
                className={styles.selectIcon}
                src={iconUpsellModalSelectIcon}
            />
        );
        const analyticsClassName = ANALYTICS_CLASS_NAMES[props.bundleType];
        const buttonClasses = classNames({
            [styles.selectButton]: true,
            [styles.selectedButton]: isSelected,
            [analyticsClassName]: true,
        });

        return (
            <label className={buttonClasses} tabIndex={0} role="button">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onChange}
                />
                {icon}
                {textLabel}
            </label>
        );
    };

    const columnClasses = classNames({
        [styles.column]: true,
        [styles.multipleColumns]: !props.isOneProduct,
    });
    const headerClasses = classNames({
        [styles.columnHeader]: true,
        [styles.headerOneProduct]: props.isOneProduct && !props.isFreeGift,
        [styles.centeredHeader]: props.isFreeGift,
    });

    return (
        <div className={columnClasses}>
            <header className={headerClasses} role="group" aria-label="item">
                {props.column.flair && props.column.flair.length > 0 && (
                    <Flair flair={props.column.flair[0].value} inline={true} />
                )}
                {product && (
                    <UpsellInfoColumnProductInfo
                        product={product}
                        baseVariant={baseVariant}
                        selectedRoot={props.selectedRoot}
                        bundleType={props.bundleType}
                        isOneProduct={props.isOneProduct}
                    />
                )}
                <div>
                    {product &&
                        props.bundleType !==
                            BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT &&
                        buildUI()}
                </div>
            </header>
            <UpsellInfoColumnContent
                isOneProduct={props.isOneProduct}
                content={props.column.content}
            />
        </div>
    );
};

export const UpsellInfoModalContent = (props: {
    baseVariant: IProduct | null;
    selectedRoot: IProduct | null;
    modal: IModularConfiguratorUpsellInfoModal;
    selectedAddons: readonly IAddon[];
    selectedUpgrade: IProductID | null;
    upgradeBundles: readonly IConcreteBundle[];
    updateSelectedAddons: (
        updates: Array<{ productID: IProductID; quantity: number }>,
    ) => void;
    setSelectedUpgrade: (selectedUpgrade: IProductID | null) => void;
}): React.ReactNode => {
    return (
        <>
            <h1 className={styles.title}>{props.modal.bundle_description}</h1>
            <div className={styles.container}>
                {props.modal.upsell_info_products.map((column, i) => {
                    return (
                        <UpsellInfoColumn
                            key={`column-${i}`}
                            column={column}
                            isOneProduct={
                                props.modal.upsell_info_products.length < 2
                            }
                            isFreeGift={
                                props.modal.bundle_type ===
                                BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT
                            }
                            bundleID={props.modal.bundle}
                            bundleType={props.modal.bundle_type}
                            baseVariant={props.baseVariant}
                            selectedRoot={props.selectedRoot}
                            selectedAddons={props.selectedAddons}
                            selectedUpgrade={props.selectedUpgrade}
                            upgradeBundles={props.upgradeBundles}
                            updateSelectedAddons={props.updateSelectedAddons}
                            setSelectedUpgrade={props.setSelectedUpgrade}
                        />
                    );
                })}
            </div>
        </>
    );
};
